import type { Ref } from 'vue'
import { throttle } from 'lodash-es'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

// Define types for the sections
type SectionId = string

interface Section {
  id: SectionId
  label?: string

  [key: string]: any
}

type SectionInput = SectionId | Section

interface ScrollSpyOptions {
  offsetPx?: number | (() => number)
  throttleMs?: number
  visibilityThreshold?: number
}

// Return type of the composable
interface ScrollSpyReturn {
  activeSection: Ref<SectionId>
  scrollToSection: (sectionId: SectionId) => void
}

/**
 * A composable for tracking which sections are currently visible in the viewport
 * and providing navigation functionality
 *
 * @param sections  The sections to track
 * @param options   Configuration options
 * @returns Active section and navigation methods
 */
export function useScrollSpy(sections: SectionInput[], options: ScrollSpyOptions = {}): ScrollSpyReturn {
  const {
    offsetPx = -10,
    throttleMs = 100,
    visibilityThreshold = 0.6,
  } = options

  const getOffset = (): number =>
    typeof offsetPx === 'function' ? offsetPx() : offsetPx

  const activeSection = ref<SectionId>('')
  const isUserScrolling = ref(true)

  function getSectionId(section: SectionInput): SectionId {
    return typeof section === 'string' ? section : section.id
  }

  function scrollToSection(sectionId: SectionId): void {
    activeSection.value = sectionId
    isUserScrolling.value = false

    const element = document.getElementById(sectionId)
    if (element) {
      const rect = element.getBoundingClientRect()
      const absoluteTop = window.scrollY + rect.top - getOffset()

      window.scrollTo({
        top: absoluteTop,
        behavior: 'smooth',
      })

      setTimeout(() => {
        isUserScrolling.value = true
      }, 1000)
    }
  }

  /**
   * Pick the section with the highest visible-area ratio, but only if it clears
   * `visibilityThreshold`. Otherwise fall back to the last section the user has
   * scrolled past, so the highlight doesn't ping-pong while transitioning between
   * two short sections.
   */
  function calculateActiveSection(): void {
    if (!isUserScrolling.value) {
      return
    }

    const offset = getOffset()
    const viewportHeight = window.innerHeight
    const usableViewport = Math.max(viewportHeight - offset, 1)

    interface Measured { id: string, top: number, ratio: number }
    const measured: Measured[] = []

    sections.forEach((section) => {
      const id = getSectionId(section)
      const element = document.getElementById(id)
      if (!element) {
        return
      }

      const rect = element.getBoundingClientRect()
      const visibleTop = Math.max(rect.top, offset)
      const visibleBottom = Math.min(rect.bottom, viewportHeight)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)
      const denominator = Math.min(rect.height, usableViewport)
      const ratio = denominator > 0 ? visibleHeight / denominator : 0

      measured.push({ id, top: rect.top, ratio })
    })

    if (measured.length === 0) {
      return
    }

    const best = measured.reduce((a, b) => (b.ratio > a.ratio ? b : a))

    let activeSectionId: string | null = null

    if (best.ratio >= visibilityThreshold) {
      activeSectionId = best.id
    }
    else {
      const sorted = [...measured].sort((a, b) => a.top - b.top)
      const aboveViewport = sorted.filter(section => section.top <= offset)
      if (aboveViewport.length > 0) {
        activeSectionId = aboveViewport.at(-1)?.id ?? null
      }
      else {
        activeSectionId = sorted[0]?.id ?? null
      }
    }

    if (activeSectionId) {
      activeSection.value = activeSectionId
    }
    else if (sections.length > 0) {
      const firstSection = sections[0]
      if (firstSection) {
        activeSection.value = getSectionId(firstSection)
      }
    }
  }

  const throttledCalculateActiveSection = throttle(calculateActiveSection, throttleMs)

  function handleUserInteraction() {
    isUserScrolling.value = true
  }

  // Suppress the spy while a hash-driven smooth scroll is in progress; otherwise
  // intermediate sections briefly win the visibility ratio as the page animates
  // past them and the highlight flickers. Covers nav clicks, browser back/forward,
  // deep links and manual URL edits.
  const route = useRoute()
  const stopHashWatcher = watch(() => route.hash, (hash) => {
    if (!hash) {
      return
    }
    const id = hash.slice(1)
    if (sections.some(section => getSectionId(section) === id)) {
      activeSection.value = id
      isUserScrolling.value = false
      setTimeout(() => {
        isUserScrolling.value = true
      }, 1000)
    }
  })

  onMounted(() => {
    void nextTick(() => {
      window.addEventListener('scroll', throttledCalculateActiveSection, { passive: true })
      window.addEventListener('mousedown', handleUserInteraction, { passive: true })
      window.addEventListener('keydown', handleUserInteraction, { passive: true })
      window.addEventListener('touchstart', handleUserInteraction, { passive: true })

      calculateActiveSection()
      setTimeout(calculateActiveSection, 300)
    })
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', throttledCalculateActiveSection)
    window.removeEventListener('mousedown', handleUserInteraction)
    window.removeEventListener('keydown', handleUserInteraction)
    window.removeEventListener('touchstart', handleUserInteraction)
    stopHashWatcher()
  })

  return {
    activeSection,
    scrollToSection,
  }
}
